import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Monitor, Smartphone } from 'lucide-react';
import { StatusBadge } from '../../components/StatusBadge';
import { BlockEditor } from './BlockEditor';
import {
  ADDABLE_BLOCKS,
  BLOCK_LABEL,
  insertBlock,
  moveBlock,
  newBlock,
  removeBlock,
  replaceBlock,
  sameDoc,
  spliceAt,
  tidy,
} from './emailDoc';
import {
  messageFrom,
  issuesFrom,
  previewTemplate,
  publishTemplate,
  readTemplate,
  resetTemplate,
  restoreVersion,
} from './api';
import type {
  EmailBlockType,
  EmailDoc,
  EmailDocIssue,
  EmailLanguage,
  EmailPreview,
  EmailTemplateDetail,
} from './types';

const LANGUAGES: EmailLanguage[] = ['en', 'es'];

interface Props {
  templateKey: string;
  onBack: () => void;
  /** Called after anything is published, restored or reset, so the gallery refreshes. */
  onChanged: () => void;
}

const effectiveDoc = (detail: EmailTemplateDetail, language: EmailLanguage): EmailDoc =>
  detail.published[language] ?? detail.defaults[language];

const fieldPath = (path: string) => (path ? path.replace(/\[(\d+)\]/g, ' #$1').replace(/\./g, ' → ') : 'Template');

/**
 * The editor for one email: structure on the left (subject, hero, blocks), the real render on the
 * right, updated as you type. Nothing reaches a recipient until Publish; Publish is refused with
 * the reasons if the email would break (an unknown variable, a missing required link, a non-https
 * button). Every publish is a version, and Restore publishes an old one again.
 */
export function EmailTemplateEditor({ templateKey, onBack, onChanged }: Props) {
  const [detail, setDetail] = useState<EmailTemplateDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [language, setLanguage] = useState<EmailLanguage>('en');
  const [drafts, setDrafts] = useState<Partial<Record<EmailLanguage, EmailDoc>>>({});
  const [note, setNote] = useState('');
  const [preview, setPreview] = useState<EmailPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [busy, setBusy] = useState(false);
  const [publishError, setPublishError] = useState<{ message: string; issues: EmailDocIssue[] } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [adding, setAdding] = useState<EmailBlockType | ''>('');

  const lastField = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  const load = useCallback(async () => {
    try {
      const d = await readTemplate(templateKey);
      setDetail(d);
      setDrafts({ en: effectiveDoc(d, 'en'), es: effectiveDoc(d, 'es') });
      setLoadError(null);
    } catch (err) {
      setLoadError(messageFrom(err));
    }
  }, [templateKey]);

  useEffect(() => {
    void load();
  }, [load]);

  const draft = drafts[language];
  const baseline = detail ? effectiveDoc(detail, language) : null;
  const dirty = !!draft && !!baseline && !sameDoc(draft, baseline);
  const customised = !!detail?.published[language];

  // Live preview, debounced; a response that arrives after a newer edit is dropped.
  const requestId = useRef(0);
  useEffect(() => {
    if (!detail || !draft) return;
    const id = ++requestId.current;
    setPreviewing(true);
    const timer = setTimeout(() => {
      previewTemplate(templateKey, language, tidy(draft))
        .then((result) => {
          if (id === requestId.current) setPreview(result);
        })
        .catch((err) => {
          if (id === requestId.current)
            setPreview({ issues: [{ language, path: '', message: messageFrom(err) }], subject: null, html: null });
        })
        .finally(() => {
          if (id === requestId.current) setPreviewing(false);
        });
    }, 400);
    return () => clearTimeout(timer);
  }, [detail, draft, language, templateKey]);

  const setDraft = (next: EmailDoc) => {
    setDrafts((d) => ({ ...d, [language]: next }));
    setPublishError(null);
    setNotice(null);
  };

  const insertVariable = (name: string) => {
    const el = lastField.current;
    if (!el || !document.contains(el)) {
      setNotice('Click into a field first, then pick a variable to insert it there.');
      return;
    }
    const { value, caret } = spliceAt(el.value, el.selectionStart ?? el.value.length, el.selectionEnd ?? el.value.length, `{{${name}}}`);
    // Go through the native setter and an input event so React's own onChange runs with fresh state.
    const setter = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(el), 'value')?.set;
    setter?.call(el, value);
    el.dispatchEvent(new Event('input', { bubbles: true }));
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(caret, caret);
    });
  };

  const afterChange = async (message: string) => {
    await load();
    onChanged();
    setNotice(message);
    setPublishError(null);
  };

  const publish = async () => {
    if (!draft) return;
    setBusy(true);
    setPublishError(null);
    try {
      await publishTemplate(templateKey, language, tidy(draft), note.trim() || undefined);
      setNote('');
      await afterChange(`Published. New ${language.toUpperCase()} emails use this version from now on.`);
    } catch (err) {
      setPublishError({ message: messageFrom(err), issues: issuesFrom(err) });
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (!window.confirm(`Return the ${language.toUpperCase()} email to the text shipped with the platform? Your published versions stay in the history.`)) return;
    setBusy(true);
    try {
      await resetTemplate(templateKey, language);
      await afterChange(`The ${language.toUpperCase()} email is back on the shipped text.`);
    } catch (err) {
      setPublishError({ message: messageFrom(err), issues: [] });
    } finally {
      setBusy(false);
    }
  };

  const restore = async (versionId: string) => {
    setBusy(true);
    try {
      await restoreVersion(templateKey, versionId);
      await afterChange('That version is live again.');
    } catch (err) {
      setPublishError({ message: messageFrom(err), issues: [] });
    } finally {
      setBusy(false);
    }
  };

  const versions = useMemo(
    () => (detail?.versions ?? []).filter((v) => v.language === language),
    [detail, language],
  );

  if (loadError) {
    return (
      <div>
        <div className="error-banner">{loadError}</div>
        <button type="button" className="btn btn-secondary" onClick={onBack}>Back to the gallery</button>
      </div>
    );
  }
  if (!detail || !draft) return <div className="empty-state">Loading email…</div>;

  const issues = preview?.issues ?? [];
  const issuesFor = (prefix: string) => issues.filter((i) => i.path === prefix || i.path.startsWith(`${prefix}.`) || i.path.startsWith(`${prefix}[`));

  return (
    <div data-testid="email-editor" onFocus={(e) => {
      const t = e.target;
      if (t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement) lastField.current = t;
    }}>
      <button type="button" className="et-back" onClick={onBack}>
        <ArrowLeft size={14} aria-hidden /> All emails
      </button>

      <div className="section-header">
        <div>
          <h1>{detail.name}</h1>
          <p>{detail.trigger}</p>
        </div>
        <div className="dashboard-links">
          <StatusBadge label={customised ? `${language.toUpperCase()} customised` : `${language.toUpperCase()} shipped text`} tone={customised ? 'good' : 'neutral'} />
          <div className="ps-seg" role="group" aria-label="Language">
            {LANGUAGES.map((code) => (
              <button key={code} type="button" className={language === code ? 'active' : undefined} aria-pressed={language === code} onClick={() => { setLanguage(code); setPreview(null); setPublishError(null); setNotice(null); }} data-testid={`email-lang-${code}`}>
                {code.toUpperCase()}
                {detail.published[code] ? ' ●' : ''}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="et-workbench">
        <div className="et-col">
          <section className="form-card">
            <h2>Subject and header</h2>
            <label className="et-field">
              Subject
              <input className="et-input" value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} data-testid="email-subject" />
            </label>
            <div className="et-row">
              <label className="et-field">
                Label in the brand bar
                <input className="et-input" value={draft.eyebrow ?? ''} onChange={(e) => setDraft({ ...draft, eyebrow: e.target.value })} />
              </label>
              <label className="et-field">
                Small line above the headline
                <input className="et-input" value={draft.kicker ?? ''} onChange={(e) => setDraft({ ...draft, kicker: e.target.value })} />
              </label>
            </div>
            <label className="et-field">
              Headline (the cyan block — leave empty for a quieter email)
              <input className="et-input" value={draft.headline ?? ''} onChange={(e) => setDraft({ ...draft, headline: e.target.value })} data-testid="email-headline" />
            </label>
            {['subject', 'eyebrow', 'kicker', 'headline'].flatMap((f) => issuesFor(f)).map((i, n) => (
              <p className="et-issue" key={n}>{fieldPath(i.path)}: {i.message}</p>
            ))}
          </section>

          <section className="form-card">
            <h2>Variables</h2>
            <p className="ps-card-note">
              Click a field, then a variable, to insert it there. Values are filled in per recipient and are always escaped.
              Use <code>**bold**</code> for emphasis and <code>[label](https://…)</code> for a link.
            </p>
            <div className="et-vars">
              {detail.variables.map((v) => (
                <button
                  key={v.name}
                  type="button"
                  className={`ps-chip et-var${v.required ? ' token' : ''}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => insertVariable(v.name)}
                  title={`${v.description}${v.required ? ' (required)' : ''} · e.g. ${v.sample[language]}`}
                  data-testid={`email-var-${v.name}`}
                >
                  {`{{${v.name}}}`}{v.required ? ' *' : ''}
                </button>
              ))}
            </div>
          </section>

          <section className="form-card">
            <h2>Body</h2>
            {draft.blocks.map((block, i) => (
              <BlockEditor
                key={i}
                block={block}
                index={i}
                count={draft.blocks.length}
                slots={detail.slots}
                onChange={(b) => setDraft(replaceBlock(draft, i, b))}
                onMove={(to) => setDraft(moveBlock(draft, i, to))}
                onRemove={() => setDraft(removeBlock(draft, i))}
              />
            ))}
            {issuesFor('blocks').map((i, n) => (
              <p className="et-issue" key={n}>{fieldPath(i.path)}: {i.message}</p>
            ))}
            <label className="et-field narrow">
              Add a block
              <select
                className="et-input"
                value={adding}
                onChange={(e) => {
                  const type = e.target.value as EmailBlockType | '';
                  setAdding('');
                  if (type) setDraft(insertBlock(draft, newBlock(type, detail.slots)));
                }}
                data-testid="email-add-block"
              >
                <option value="">Choose…</option>
                {ADDABLE_BLOCKS.map((t) => (
                  <option key={t} value={t}>{BLOCK_LABEL[t]}</option>
                ))}
              </select>
            </label>
          </section>

          <section className="form-card">
            <h2>Footer</h2>
            <label className="et-field">
              Why the recipient got this (leave empty for “you have a MedicLynkOS account”)
              <textarea className="et-input" rows={2} value={draft.footer ?? ''} onChange={(e) => setDraft({ ...draft, footer: e.target.value })} />
            </label>
          </section>

          <section className="form-card">
            <div className="ps-card-head">
              <h2>History · {language.toUpperCase()}</h2>
            </div>
            {versions.length === 0 ? (
              <p className="ps-card-note">Nothing published yet: this email is on the text shipped with the platform.</p>
            ) : (
              <div className="ps-versions">
                {versions.map((v) => (
                  <div key={v.id} className={`ps-version${v.status === 'published' ? ' published' : ''}`}>
                    <div className="ps-version-main">
                      <div className="ps-version-title">
                        {v.doc.subject}
                        {v.status === 'published' && <StatusBadge label="Live" tone="good" />}
                      </div>
                      {v.note && <p className="ps-version-note">{v.note}</p>}
                      <p className="ps-version-meta">{new Date(v.createdAt).toLocaleString()} · {v.createdBy}</p>
                    </div>
                    {v.status !== 'published' && (
                      <div className="ps-version-actions">
                        <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => void restore(v.id)}>
                          Restore
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        <aside className="et-preview-col">
          <div className="et-preview-bar">
            <span className="et-preview-title">Preview · sample data</span>
            <span className="ps-seg" role="group" aria-label="Preview width">
              <button type="button" className={device === 'desktop' ? 'active' : undefined} aria-pressed={device === 'desktop'} onClick={() => setDevice('desktop')} aria-label="Desktop width"><Monitor size={12} aria-hidden /></button>
              <button type="button" className={device === 'mobile' ? 'active' : undefined} aria-pressed={device === 'mobile'} onClick={() => setDevice('mobile')} aria-label="Phone width"><Smartphone size={12} aria-hidden /></button>
            </span>
          </div>
          <div className="et-inbox-row" data-testid="email-preview-subject">
            <span className="et-inbox-from">MedicLynk</span>
            <span className="et-inbox-subject">{preview?.subject ?? '…'}</span>
          </div>
          <div className={`et-frame-wrap ${device}`}>
            {preview?.html ? (
              <iframe title="Email preview" sandbox="" srcDoc={preview.html} className="et-frame" data-testid="email-preview-frame" />
            ) : (
              <div className="et-frame-empty">{previewing ? 'Rendering…' : 'Fix the problems below to see the email.'}</div>
            )}
          </div>

          {issues.length > 0 && (
            <div className="ps-warning" data-testid="email-issues">
              {issues.map((i, n) => (
                <p key={n}><strong>{fieldPath(i.path)}:</strong> {i.message}</p>
              ))}
            </div>
          )}
          {publishError && (
            <div className="error-banner" data-testid="email-publish-error">
              {publishError.message}
              {publishError.issues.length > 0 && (
                <ul style={{ margin: '0.4rem 0 0', paddingLeft: '1.1rem' }}>
                  {publishError.issues.map((i, n) => (
                    <li key={n}>{fieldPath(i.path)}: {i.message}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {notice && <p className="et-notice" data-testid="email-notice">{notice}</p>}

          <div className="et-publish">
            <label className="et-field">
              Note (optional)
              <input className="et-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="What this change is for" />
            </label>
            <div className="ps-actions">
              <button type="button" className="btn btn-primary" onClick={() => void publish()} disabled={busy || !dirty || issues.length > 0} data-testid="email-publish">
                {busy ? 'Working…' : `Publish ${language.toUpperCase()}`}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => baseline && setDraft(baseline)} disabled={busy || !dirty}>
                Discard edits
              </button>
              {customised && (
                <button type="button" className="btn btn-secondary" onClick={() => void reset()} disabled={busy} data-testid="email-reset">
                  Reset to shipped text
                </button>
              )}
            </div>
            <p className="ps-card-note">
              {dirty ? 'You have unpublished changes.' : 'No unpublished changes.'} Publishing takes effect immediately and applies to {language === 'en' ? 'English' : 'Spanish'} recipients only.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
